<template>
    <!-- Loading State -->
    <div v-if="loading" class="recipe-loading">
        <LoadingStates type="skeleton-card" />
    </div>

    <!-- Recipe Card -->
    <div v-else class="recipe-card" @click="handleClick" @keydown="handleKeydown" tabindex="0">

        <!-- Image area -->
        <div class="recipe-image-wrap">
            <img v-if="recipe.image" :src="recipe.image" :alt="recipe.name" class="recipe-image" />
            <div v-else class="recipe-placeholder">
                <span class="recipe-placeholder-text">{{ getInitials(recipe.name) }}</span>
            </div>
            <span class="recipe-category-badge">
                {{ recipe.categoryName || formatCategoryName(recipe.category) }}
            </span>
        </div>

        <!-- Content -->
        <div class="recipe-content">
            <div class="recipe-name line-clamp-1">{{ recipe.name }}</div>
            <div class="recipe-description line-clamp-2">
                {{ recipe.description || 'No description available' }}
            </div>

            <!-- Plan Meal button -->
            <button @click.stop="handlePlan" class="recipe-plan-btn">
                <svg width="13" height="13" fill="none" viewBox="0 0 24 24">
                    <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 3c1.93 0 3.5 1.57 3.5 3.5S13.93 13 12 13s-3.5-1.57-3.5-3.5S10.07 6 12 6zm7 13H5v-.23c0-.62.28-1.2.76-1.58C7.47 15.82 9.64 15 12 15s4.53.82 6.24 2.19c.48.38.76.97.76 1.58V19z" fill="white"/>
                </svg>
                Plan Meal
            </button>

            <!-- Ingredients count -->
            <div class="recipe-meta">
                <svg width="11" height="11" fill="none" viewBox="0 0 24 24">
                    <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2" stroke="#94A3B8" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
                {{ recipe.ingredientCount }} ingredient{{ recipe.ingredientCount !== 1 ? 's' : '' }}
                <span v-if="recipe.difficulty" class="recipe-difficulty">· {{ recipe.difficulty }}</span>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import LoadingStates from './LoadingStates.vue'

const getInitials = (name: string) =>
    name.split(' ').filter(w => w && /^[A-ZÀ-Ú]/u.test(w)).map(w => w[0].toUpperCase()).slice(0, 3).join('')

export interface Recipe {
    id: string
    name: string
    description?: string
    image?: string
    category: 'vegetables' | 'mainDishes' | 'fish' | 'breakfast'
    categoryName?: string
    cookTime?: number
    ingredientCount: number
    difficulty?: 'Easy' | 'Medium' | 'Hard'
    isFavorite?: boolean
}

export interface RecipeCardProps {
    recipe: Recipe
    loading?: boolean
}

const props = defineProps<RecipeCardProps>()

const emit = defineEmits<{
    click: [recipe: Recipe]
    plan: [recipe: Recipe]
}>()

const handlePlan = (event: MouseEvent) => {
    event.stopPropagation()
    emit('plan', props.recipe)
}

const formatCategoryName = (category: string) => {
    const categoryNames: Record<string, string> = {
        vegetables: 'Vegetables',
        mainDishes: 'Main Dishes',
        fish: 'Fish',
        breakfast: 'Breakfast'
    }
    return categoryNames[category] || category
}

const handleClick = () => {
    emit('click', props.recipe)
}

const handleKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        handleClick()
    }
}
</script>

<style scoped>
/* ── Loading wrapper ── */
.recipe-loading { background: white; border-radius: 16px; border: 1px solid rgba(226,232,240,.8); overflow: hidden; }

/* ── Card base ── */
.recipe-card {
    background: white;
    border-radius: 16px;
    border: 1px solid rgba(226,232,240,.8);
    overflow: hidden;
    cursor: pointer;
    transition: box-shadow .2s, transform .2s;
    display: flex;
    flex-direction: column;
}
.recipe-card:hover        { box-shadow: 0 10px 28px rgba(79,134,70,.18); transform: translateY(-3px); }
.recipe-card:focus-visible { outline: 2px solid #4F8646; outline-offset: 2px; }

/* ── Image area ── */
.recipe-image-wrap { height: 150px; position: relative; overflow: hidden; }
.recipe-image      { width: 100%; height: 100%; object-fit: cover; }

.recipe-placeholder      { width: 100%; height: 100%; background: linear-gradient(135deg,rgba(79,134,70,.3),rgba(79,134,70,.08)); display: flex; align-items: center; justify-content: center; }
.recipe-placeholder-text { font-family: Manrope, sans-serif; font-weight: 700; font-size: 30px; color: #4F8646; opacity: .6; user-select: none; }

.recipe-category-badge { position: absolute; top: 12px; right: 12px; background: #EDF3EC; color: #4F8646; border-radius: 9999px; padding: 3px 10px; font-size: 11px; font-weight: 700; font-family: Inter, sans-serif; }

/* ── Content ── */
.recipe-content     { padding: 16px 18px 18px; display: flex; flex-direction: column; flex: 1; }
.recipe-name        { font-weight: 700; font-size: 14px; color: #0F172A; margin-bottom: 4px; line-height: 1.3; }
.recipe-description { font-size: 12px; color: #94A3B8; margin-bottom: 14px; line-height: 1.5; flex: 1; }

.recipe-plan-btn {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: 36px;
    border-radius: 8px;
    background: #4F8646;
    color: white;
    border: none;
    font-family: Inter, sans-serif;
    font-weight: 700;
    font-size: 13px;
    cursor: pointer;
    transition: opacity .15s;
    margin-bottom: 10px;
}
.recipe-plan-btn:hover { opacity: .85; }

.recipe-meta       { font-size: 11px; color: #94A3B8; display: flex; align-items: center; gap: 4px; }
.recipe-difficulty { margin-left: 8px; }

/* ── Clamp utilities ── */
.line-clamp-1 { display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden; }
.line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
</style>